import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { filesAPI, adminAPI, ticketsAPI } from '../../services/api';
import Chart from 'react-apexcharts';
import { 
  FileUp, Files, Clock, CheckCircle, TrendingUp, 
  ArrowUpRight, ArrowDownRight, AlertCircle, Wallet,
  LifeBuoy, ChevronRight, Activity, Globe, ShoppingBag,
  Users, Timer, RefreshCw, Cpu, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'red', subtitle }) => {
  const colors = {
    red: 'bg-[#EC2229]/10 text-[#EC2229]',
    green: 'bg-emerald-500/10 text-emerald-500',
    blue: 'bg-blue-500/10 text-blue-500',
    amber: 'bg-amber-500/10 text-amber-500',
  };

  return (
    <div className="glass-card-hover p-5" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{title}</p>
          <p className="text-2xl font-heading font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs ${trend === 'up' ? 'text-emerald-500' : 'text-red-500'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

const RecentFileCard = ({ file }) => {
  const { t } = useI18n();
  const statusColors = {
    pending: 'badge-warning',
    in_progress: 'badge-info',
    completed: 'badge-success',
    revision_requested: 'badge-error',
  };
  const statusLabels = {
    pending: t('files.pending'),
    in_progress: t('files.inProgress'),
    completed: t('files.completed'),
    revision_requested: t('files.revisionRequested'),
  };

  return (
    <Link to={`/files/${file.id}`} className="glass-card p-4 hover:border-[#EC2229]/30 transition-all group" data-testid={`recent-file-${file.ticket_id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-sm text-[#EC2229]">{file.ticket_id}</span>
        <span className={statusColors[file.status]}>{statusLabels[file.status]}</span>
      </div>
      <p className="text-white font-medium group-hover:text-[#EC2229] transition-colors">
        {file.vehicle_brand} {file.vehicle_model}
      </p>
      <p className="text-sm text-slate-400 mt-1">{new Date(file.created_at).toLocaleDateString()}</p>
      {file.queue_position && file.status === 'pending' && (
        <div className="mt-2 flex items-center gap-2 text-xs text-amber-500">
          <Clock className="w-3 h-3" />
          <span>Position #{file.queue_position}</span>
        </div>
      )}
    </Link>
  );
};

// Activity feed item types with icons and colors
const ACTIVITY_CONFIG = {
  file_upload: { icon: FileUp, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  ticket: { icon: LifeBuoy, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  topup: { icon: Wallet, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  order: { icon: ShoppingBag, color: 'text-purple-400', bg: 'bg-purple-500/10' },
};

const ActivityItem = ({ activity }) => {
  const config = ACTIVITY_CONFIG[activity.type] || ACTIVITY_CONFIG.file_upload;
  const Icon = config.icon;
  const timeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'gerade eben';
    if (diff < 3600) return `vor ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)}h`;
    return `vor ${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-white/[0.04] last:border-0" data-testid={`activity-${activity.type}`}>
      <div className={`p-1.5 rounded-md mt-0.5 ${config.bg}`}>
        <Icon className={`w-3.5 h-3.5 ${config.color}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-300 truncate">{activity.message}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-slate-500 font-mono">{activity.detail}</span>
          <span className="text-[10px] text-slate-600">{timeAgo(activity.timestamp)}</span>
        </div>
      </div>
    </div>
  );
};

// Monitoring Queue Widget
const QueueMonitor = ({ monitoring }) => {
  if (!monitoring?.queue) return null;
  const q = monitoring.queue;
  const total = q.pending + q.in_progress;

  return (
    <div className="glass-card p-5" data-testid="queue-monitor">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-[#EC2229]" />
        Warteschlange Live
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-400">{q.pending}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Wartend</div>
        </div>
        <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-blue-400">{q.in_progress}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">In Arbeit</div>
        </div>
        <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-emerald-400">{q.completed_today}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Heute fertig</div>
        </div>
        <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-400">{q.revision_requested}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Revision</div>
        </div>
      </div>
      {q.avg_processing_mins > 0 && (
        <div className="mt-3 pt-3 border-t border-white/[0.04] flex items-center justify-between">
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            <Timer className="w-3 h-3" /> Avg. Bearbeitungszeit
          </span>
          <span className="text-sm font-mono text-white">{q.avg_processing_mins < 60 ? `${q.avg_processing_mins} min` : `${(q.avg_processing_mins / 60).toFixed(1)} Std`}</span>
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const [stats, setStats] = useState(null);
  const [recentFiles, setRecentFiles] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [monitoring, setMonitoring] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [chartData, setChartData] = useState([0,0,0,0,0,0,0,0,0,0,0,0]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const refreshInterval = useRef(null);
  const isAdmin = user?.role === 'admin' || user?.role === 'operator';

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);

      const [filesRes, ticketsRes] = await Promise.all([
        filesAPI.list({ limit: 5 }),
        ticketsAPI.list().catch(() => ({ data: [] })),
      ]);

      setRecentFiles(filesRes.data);
      setTickets((ticketsRes.data || []).slice(0, 3));

      if (isAdmin) {
        const [statsRes, monRes, actRes, chartRes] = await Promise.all([
          adminAPI.getStats(),
          adminAPI.getMonitoring(),
          adminAPI.getActivityFeed(15),
          adminAPI.getChartData(),
        ]);
        if (statsRes.data) setStats(statsRes.data);
        if (monRes.data) setMonitoring(monRes.data);
        if (actRes.data) setActivityFeed(actRes.data);
        if (chartRes.data?.monthly_counts) setChartData(chartRes.data.monthly_counts);
      }

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 30s for admins
    if (isAdmin) {
      refreshInterval.current = setInterval(() => fetchDashboardData(true), 30000);
    }
    return () => { if (refreshInterval.current) clearInterval(refreshInterval.current); };
  }, [user]);

  const chartOptions = {
    chart: { type: 'area', toolbar: { show: false }, background: 'transparent', fontFamily: 'Manrope, sans-serif' },
    colors: ['#EC2229'],
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 100] } },
    stroke: { curve: 'smooth', width: 2 },
    grid: { borderColor: 'rgba(255,255,255,0.05)', strokeDashArray: 4 },
    xaxis: {
      categories: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
      labels: { style: { colors: '#64748B' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: '#64748B' } } },
    dataLabels: { enabled: false },
    tooltip: { theme: 'dark', style: { fontFamily: 'Manrope' } },
  };

  const chartSeries = [{ name: t('dashboard.filesProcessed'), data: chartData }];
  const formatBalance = (balance) => `${(balance || 0).toFixed(1)} Credits`;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#EC2229] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">
              {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-400 mt-1">Hier ist Ihre Übersicht für heute</p>
          </div>
          <div className="flex gap-3 items-center">
            {isAdmin && lastRefresh && (
              <button
                onClick={() => fetchDashboardData(true)}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                data-testid="refresh-dashboard"
              >
                <RefreshCw className="w-3 h-3" />
                {lastRefresh.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
              </button>
            )}
            <Link to="/wallet">
              <Button variant="outline" className="btn-secondary" data-testid="topup-btn">
                <Wallet className="w-4 h-4 mr-2" />
                {t('dashboard.topUp')}
              </Button>
            </Link>
            <Link to="/upload">
              <Button className="btn-primary" data-testid="upload-btn">
                <FileUp className="w-4 h-4 mr-2" />
                {t('files.uploadNew')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title={t('dashboard.balance')}
            value={formatBalance(user?.balance)}
            icon={Wallet}
            color="red"
          />
          <StatCard
            title={t('dashboard.todayFiles')}
            value={stats?.files_today || recentFiles.filter(f => new Date(f.created_at).toDateString() === new Date().toDateString()).length}
            icon={FileUp}
            color="green"
          />
          <StatCard
            title={t('dashboard.pendingFiles')}
            value={stats?.pending_files || recentFiles.filter(f => f.status === 'pending').length}
            icon={Clock}
            color="amber"
          />
          <StatCard
            title={t('dashboard.monthFiles')}
            value={stats?.files_month || recentFiles.length}
            icon={TrendingUp}
            color="blue"
          />
        </div>

        {/* Admin Monitoring Section */}
        {isAdmin && monitoring && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="monitoring-section">
            <StatCard
              title="Offene Tickets"
              value={monitoring.tickets?.open || 0}
              icon={LifeBuoy}
              color="amber"
              subtitle={monitoring.tickets?.unanswered > 0 ? `${monitoring.tickets.unanswered} unbeantwortet` : undefined}
            />
            <StatCard
              title="Aktive Nutzer"
              value={monitoring.business?.active_uploaders_this_week || 0}
              icon={Users}
              color="blue"
              subtitle="Diese Woche"
            />
            <StatCard
              title="Credits Woche"
              value={`${(monitoring.business?.credits_this_week || 0).toFixed(0)}`}
              icon={Zap}
              color="green"
              subtitle="Eingegangen"
            />
            <StatCard
              title="Offene Bestellungen"
              value={monitoring.business?.pending_orders || 0}
              icon={ShoppingBag}
              color="red"
              subtitle="Shop"
            />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <div className="lg:col-span-2 glass-card p-6">
            <h2 className="font-heading text-lg font-semibold text-white mb-4">
              {t('dashboard.monthlyOverview')}
            </h2>
            <Chart options={chartOptions} series={chartSeries} type="area" height={280} />
          </div>

          {/* Queue Monitor (Admin) or Service Status (User) */}
          {isAdmin && monitoring ? (
            <QueueMonitor monitoring={monitoring} />
          ) : (
            <div className="glass-card p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">
                {t('dashboard.serviceStatus')}
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                  <div>
                    <p className="text-emerald-500 font-medium">{t('dashboard.online')}</p>
                    <p className="text-xs text-slate-400">Service läuft normal</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Bearbeitungszeit</span>
                    <span className="text-white font-mono">~15 min</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Warteschlange</span>
                    <span className="text-white font-mono">{stats?.pending_files || 0} Dateien</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Verfügbarkeit</span>
                    <span className="text-emerald-500 font-mono">99.9%</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Admin: Activity Feed + Scraper Status */}
        {isAdmin && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Activity Feed */}
            <div className="glass-card p-5" data-testid="activity-feed">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#EC2229]" />
                  Letzte Aktivitäten
                </h2>
                {lastRefresh && (
                  <span className="text-[10px] text-slate-600 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live
                  </span>
                )}
              </div>
              <div className="max-h-[320px] overflow-y-auto pr-1 scrollbar-thin">
                {activityFeed.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">Keine Aktivitäten</p>
                ) : (
                  activityFeed.map((activity, i) => (
                    <ActivityItem key={`${activity.type}-${i}`} activity={activity} />
                  ))
                )}
              </div>
            </div>

            {/* Scraper + DB Status */}
            <div className="glass-card p-5" data-testid="scraper-monitor">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-400" />
                  Fahrzeug-Datenbank
                </h2>
                <Link to="/admin" className="text-xs text-[#EC2229] hover:underline flex items-center gap-1" data-testid="scraper-widget-link">
                  Verwalten <ChevronRight className="w-3 h-3" />
                </Link>
              </div>

              {monitoring?.scraper && (
                <div className="space-y-3">
                  {/* Total */}
                  <div className="bg-white/[0.03] rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-500/10 p-2 rounded-lg">
                        <Cpu className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <div className="text-xl font-bold text-white">{monitoring.scraper.total_vehicles.toLocaleString()}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Fahrzeuge gesamt</div>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${monitoring.scraper.is_running ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${monitoring.scraper.is_running ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`} />
                      {monitoring.scraper.is_running ? 'Scraper läuft...' : 'Bereit'}
                    </div>
                  </div>

                  {/* Sources breakdown */}
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(monitoring.scraper.by_source || {}).map(([src, cnt]) => (
                      <div key={src} className="bg-white/[0.03] rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-white">{cnt.toLocaleString()}</div>
                        <div className="text-[10px] text-slate-500 capitalize">{src}</div>
                      </div>
                    ))}
                  </div>

                  {/* Last run info */}
                  {monitoring.scraper.last_run?.completed_at && (
                    <div className="flex items-center justify-between text-xs pt-2 border-t border-white/[0.04]">
                      <span className="text-slate-500">Letzter Lauf</span>
                      <span className="text-slate-300 font-mono">
                        {new Date(monitoring.scraper.last_run.completed_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        {monitoring.scraper.last_run.triggered_by && (
                          <span className="text-slate-500 ml-1">({monitoring.scraper.last_run.triggered_by})</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Actions Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Open Support Tickets */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-[#EC2229]" />
                Support-Tickets
              </h2>
              <Link to="/support" className="text-sm text-[#EC2229] hover:underline flex items-center gap-1" data-testid="view-all-tickets">
                Alle <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            {tickets.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Keine offenen Tickets</p>
            ) : (
              <div className="space-y-2">
                {tickets.map(t => (
                  <Link key={t.id} to="/support" className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5 hover:border-white/15 transition-all" data-testid={`dashboard-ticket-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{t.subject}</p>
                      <p className="text-xs text-slate-500">{t.ticket_number}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ml-3 ${t.status === 'open' ? 'bg-amber-500/10 text-amber-400' : t.status === 'answered' ? 'bg-blue-500/10 text-blue-400' : 'bg-slate-500/10 text-slate-400'}`}>
                      {t.status === 'open' ? 'Offen' : t.status === 'answered' ? 'Beantwortet' : 'Geschlossen'}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Pending Files Quick View */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Offene Aufträge
              </h2>
              <Link to="/files" className="text-sm text-[#EC2229] hover:underline flex items-center gap-1" data-testid="view-pending-files">
                Alle <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            {(() => {
              const pendingFiles = recentFiles.filter(f => f.status === 'pending' || f.status === 'in_progress');
              return pendingFiles.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">Keine offenen Aufträge</p>
              ) : (
                <div className="space-y-2">
                  {pendingFiles.slice(0, 3).map(f => (
                    <Link key={f.id} to={`/files/${f.id}`} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/5 hover:border-white/15 transition-all" data-testid={`dashboard-pending-${f.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{f.vehicle_brand} {f.vehicle_model}</p>
                        <p className="text-xs text-slate-500 font-mono">{f.ticket_id}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ml-3 ${f.status === 'pending' ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'}`}>
                        {f.status === 'pending' ? 'Wartend' : 'In Bearbeitung'}
                      </span>
                    </Link>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Recent Files */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-white">
              {t('dashboard.recentFiles')}
            </h2>
            <Link to="/files" className="text-sm text-[#EC2229] hover:underline flex items-center gap-1" data-testid="view-all-files">
              Alle anzeigen
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          {recentFiles.length === 0 ? (
            <div className="text-center py-12">
              <Files className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">{t('files.noFiles')}</p>
              <Link to="/upload">
                <Button className="btn-primary mt-4">
                  <FileUp className="w-4 h-4 mr-2" />
                  Erste Datei hochladen
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {recentFiles.map(file => (
                <RecentFileCard key={file.id} file={file} />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
